/**
 * The parse draft — one document under construction, shared by all eleven parsers.
 *
 * Eleven parsers each assembling a `SubtitleDocument` by hand is eleven chances to forget the
 * synthetic style table, eleven interpretations of `InputRef.cueCount`, and eleven places for
 * a diagnostic to go missing. CORE gives us `emptyDocument`, which is the right starting
 * point; this adds the accumulation and the one exit.
 *
 * PURITY: no DOM, no Blob, no fetch, no Node. `src/formats/**` is checked under the base
 * tsconfig, whose `lib` is ES-only, so that is enforced by the compiler rather than by
 * discipline — and there is a test that checks it independently.
 */

import type {
  Cue,
  Diagnostic,
  DiagnosticCode,
  Framerate,
  IdAllocator,
  InputId,
  LossEntry,
  ParseOptions,
  ParseResult,
  ReadFormat,
  Region,
  Scope,
  Severity,
  Style,
  StyleId,
  StyleTable,
  SubtitleDocument,
  Ticks,
  Timebase,
  Track,
  TrackId,
} from '../../../contracts/cue-model.ts';
import {
  emptyDocument,
  makeTrack,
  syntheticStyle,
  UNKNOWN_TIMEBASE,
} from '../../core/model/build.ts';
import { sortCues } from '../../core/model/lookup.ts';
import { T } from '../../core/time/ticks.ts';

export interface DraftInit {
  readonly alloc: IdAllocator;
  readonly opts: ParseOptions;
  readonly sourceFormat: ReadFormat;
  /** The exact lattice the parse produced. `MS_GRID` for srt/vtt/sbv/smi, `CS_GRID` for
   *  ass/lrc, `DS_GRID` for mpl2, the file's own fps for MicroDVD. */
  readonly grid?: Framerate | null;
  readonly timebase?: Timebase;
  readonly title?: string | null;
}

/**
 * One document under construction.
 *
 * Mutable on purpose and never escapes a parser: `finish()` produces the frozen-shaped
 * immutable document the rest of the library requires. Building immutably here would mean a
 * fresh array per cue on a 90,000-cue ASS file.
 */
export class Draft {
  readonly alloc: IdAllocator;
  readonly opts: ParseOptions;
  readonly sourceFormat: ReadFormat;
  readonly inputId: InputId;
  /** The track every cue lands on unless the parser says otherwise (SMI classes, containers). */
  readonly defaultTrackId: TrackId;
  readonly defaultStyleId: StyleId;

  readonly cues: Cue[] = [];
  readonly diagnostics: Diagnostic[] = [];
  readonly loss: LossEntry[] = [];
  readonly regions: Region[] = [];
  /** Ordered document-level passthrough — LRC id tags, the WebVTT header, the SAMI <HEAD>. */
  readonly extra: [string, string][] = [];

  #doc: SubtitleDocument;
  #styles: Map<StyleId, Style>;
  #styleOrder: StyleId[];
  #tracks: Track[];
  #grid: Framerate | null;
  #timebase: Timebase;
  #title: string | null;
  #durationTicks: Ticks | null = null;

  constructor(init: DraftInit) {
    this.alloc = init.alloc;
    this.opts = init.opts;
    this.sourceFormat = init.sourceFormat;

    const base = emptyDocument(init.alloc, {
      inputName: init.opts.inputName,
      sourceFormat: init.sourceFormat,
    });
    this.#doc = base.doc;
    this.inputId = base.inputId;
    this.defaultTrackId = base.trackId;
    this.defaultStyleId = base.styleId;

    const style = base.doc.styles.byId[base.styleId] as Style;
    this.#styles = new Map([[base.styleId, style]]);
    this.#styleOrder = [base.styleId];
    this.#tracks = [...base.doc.tracks];
    this.#grid = init.grid ?? null;
    this.#timebase = init.timebase ?? UNKNOWN_TIMEBASE;
    this.#title = init.title ?? null;
  }

  /*── styles ──────────────────────────────────────────────────────────────────────*/

  /** Adds or replaces a style. The synthetic default is REPLACED by the first real style a
   *  format declares, so an ASS file's own "Default" is the document default rather than a
   *  second style sitting behind ours. */
  addStyle(style: Style): void {
    if (!this.#styles.has(style.id)) this.#styleOrder.push(style.id);
    this.#styles.set(style.id, style);
  }

  /** Mints a style id and registers a style built from a partial. */
  newStyle(name: string, over: Partial<Omit<Style, 'id' | 'name'>> = {}): Style {
    const id = this.alloc.style();
    const style: Style = { ...syntheticStyle(id, name), ...over, id, name, isSynthetic: false };
    this.addStyle(style);
    return style;
  }

  styleByName(name: string): Style | undefined {
    for (const id of this.#styleOrder) {
      const s = this.#styles.get(id);
      if (s !== undefined && s.name === name) return s;
    }
    return undefined;
  }

  /** Drops the synthetic default when a format supplied real styles, and points the table's
   *  default at the first declared one. */
  useDeclaredStyles(defaultId: StyleId): void {
    if (this.#styleOrder.length > 1 && this.#styleOrder[0] === this.defaultStyleId) {
      const synthetic = this.#styles.get(this.defaultStyleId);
      if (synthetic?.isSynthetic === true) {
        this.#styles.delete(this.defaultStyleId);
        this.#styleOrder = this.#styleOrder.filter((id) => id !== this.defaultStyleId);
      }
    }
    this.#declaredDefault = defaultId;
  }

  #declaredDefault: StyleId | null = null;
  #renamedDefault = false;

  /*── tracks ──────────────────────────────────────────────────────────────────────*/

  /**
   * Interns a track by NAME. SAMI's `<P Class=ENUSCC>` names its streams and MKV names its
   * tracks; both need the same id every time the name recurs, and `Track.order` must be the
   * declaration order rather than the opaque id, because that is what decides which language
   * renders on top after a merge.
   */
  trackFor(name: string, language: string | null, kind: Track['kind'] = 'subtitle'): TrackId {
    if (this.opts.singleTrack) return this.defaultTrackId;
    const existing = this.#tracks.find((t) => t.name === name);
    if (existing !== undefined) return existing.id;
    // The FIRST named track renames the synthetic default rather than sitting beside it, so a
    // single-language SAMI does not end up with two tracks and a 'multi-track-detected'
    // diagnostic it never earned. Only the first: letting the second one rename it as well is
    // how a bilingual file silently collapses into one stream, which is the exact failure the
    // track axis exists to prevent.
    const first = this.#tracks[0];
    if (!this.#renamedDefault && this.#tracks.length === 1 && first !== undefined) {
      this.#renamedDefault = true;
      const replaced: Track = { ...first, name, language, kind };
      this.#tracks = [replaced];
      return replaced.id;
    }
    const track = makeTrack(this.alloc.track(), this.#tracks.length, { name, language, kind });
    this.#tracks = [...this.#tracks, track];
    if (this.#tracks.length === 2) {
      this.diag('multi-track-detected', `this file interleaves more than one caption stream`);
    }
    return track.id;
  }

  setTrackLanguage(id: TrackId, language: string | null): void {
    this.#tracks = this.#tracks.map((t) => (t.id === id ? { ...t, language } : t));
  }

  get tracks(): readonly Track[] {
    return this.#tracks;
  }

  /*── metadata ────────────────────────────────────────────────────────────────────*/

  setTitle(title: string | null): void {
    this.#title = title;
  }

  setTimebase(tb: Timebase): void {
    this.#timebase = tb;
  }

  setGrid(grid: Framerate | null): void {
    this.#grid = grid;
  }

  setDuration(t: Ticks | null): void {
    this.#durationTicks = t;
  }

  /** Ordered document-level passthrough. Duplicate keys are KEPT: `[ar:]` twice in an LRC is
   *  the file's own content, and collapsing it would silently edit the user's document. */
  addExtra(key: string, value: string): void {
    this.extra.push([key, value]);
  }

  /*── findings ────────────────────────────────────────────────────────────────────*/

  diag(
    code: DiagnosticCode,
    message: string,
    scope: Scope = {},
    severity: Severity = 'warning',
  ): void {
    this.diagnostics.push({ code, severity, phase: 'parse', scope, message });
  }

  /** Parse-time loss. `target` is null by the frozen rule — only `project()` sets one. */
  addLoss(entry: Omit<LossEntry, 'target' | 'recoverable'> & { recoverable?: boolean }): void {
    this.loss.push({ ...entry, target: null, recoverable: entry.recoverable ?? false });
  }

  addCue(cue: Cue): void {
    this.cues.push(cue);
  }

  /*── exit ────────────────────────────────────────────────────────────────────────*/

  /** The style table as declared, with a guaranteed-present default. */
  styleTable(): StyleTable {
    const byId: Record<string, Style> = {};
    for (const id of this.#styleOrder) {
      const s = this.#styles.get(id);
      if (s !== undefined) byId[id as string] = s;
    }
    const fallback = this.#styleOrder[0] ?? this.defaultStyleId;
    const defaultId =
      this.#declaredDefault !== null && this.#styles.has(this.#declaredDefault)
        ? this.#declaredDefault
        : fallback;
    if (byId[defaultId as string] === undefined) {
      byId[defaultId as string] = syntheticStyle(defaultId);
    }
    return { byId, order: this.#styleOrder, defaultId };
  }

  /**
   * The finished document. Cues are SORTED by the total order — the contract says
   * `SubtitleDocument.cues` is flat and sorted, and a parser that hands back source order is
   * one defensive re-sort or one accidental O(n·m) scan away in four different ops.
   */
  document(): SubtitleDocument {
    const styles = this.styleTable();
    const doc: SubtitleDocument = {
      ...this.#doc,
      styles,
      tracks: this.#tracks,
      regions: this.regions,
      inputs: this.#doc.inputs.map((i) => ({
        ...i,
        format: this.sourceFormat,
        cueCount: this.cues.length,
        encoding: this.opts.encoding,
      })),
      meta: {
        ...this.#doc.meta,
        title: this.#title,
        sourceFormat: this.sourceFormat,
        durationTicks: this.#durationTicks,
        timebase: { ...this.#timebase, grid: this.#grid },
        extra: this.extra.map(([k, v]) => [k, v] as const),
      },
      loss: this.loss,
      diagnostics: this.diagnostics,
    };
    return { ...doc, cues: sortCues(doc, this.cues) };
  }

  /** The ok:true result, with the accumulated diagnostics. */
  ok(): ParseResult {
    return { ok: true, doc: this.document(), diagnostics: this.diagnostics };
  }

  /**
   * The ok:false result for a file that is not this format after all.
   *
   * `detail` is what B5's copy interpolates, so it names the LINE and what was expected —
   * "line 41: expected a timestamp like 00:01:23,456 --> 00:01:25,000". A detail that says
   * "parse failed" makes the user open a text editor with no idea what to look for.
   */
  notRecognized(detail: string): ParseResult {
    return { ok: false, reason: 'not-recognized', detail };
  }
}

/** The half-open span the model requires, with `end > start` enforced at birth.
 *  A zero- or negative-length cue is a real thing in real files; it is repaired to one
 *  millisecond and diagnosed rather than being allowed to violate the invariant. */
export function safeSpan(
  start: number,
  end: number,
  draft: Draft,
  scope: Scope,
): { start: Ticks; end: Ticks } {
  if (end > start) return { start: T.ticks(start), end: T.ticks(end) };
  draft.diag(
    end === start ? 'zero-duration' : 'negative-duration',
    end === start
      ? 'cue start and end are identical; extended to 1 ms so it can be displayed'
      : `cue ends ${(start - end) / 120} ms before it starts; extended to 1 ms`,
    scope,
  );
  return { start: T.ticks(start), end: T.ticks(start + 120) };
}
